import { useEffect } from 'react';
import { doc, onSnapshot, collection, query, where, getDocs, orderBy, limit as firestoreLimit } from 'firebase/firestore';
import { db, auth } from '../firebase/firebaseConfig';
import { restoreFromDowngrade } from '../services/downgradeService';
import { onAuthStateChanged } from 'firebase/auth';

/**
 * Hook que detecta cuando se renueva el plan y restaura items pausados automáticamente
 * 
 * Detecta upgrades de:
 * - free → familiar
 * - free → premium
 * - etc.
 * 
 * Cuando detecta un upgrade, busca el último snapshot de downgrade y restaura todo el contenido pausado.
 */
export function useAutoRestorePlan() {
  useEffect(() => {
    let unsubSnapshot: (() => void) | null = null;

    // Escuchar cambios en autenticación directamente de Firebase
    const unsubAuth = onAuthStateChanged(auth, (firebaseUser) => {
      if (unsubSnapshot) {
        unsubSnapshot();
        unsubSnapshot = null;
      }

      if (!firebaseUser?.uid) {
        return;
      }

      // Escuchar cambios en la suscripción del usuario
      unsubSnapshot = onSnapshot(
        doc(db, 'users', firebaseUser.uid),
        async (snapshot) => {
          if (!snapshot.exists()) return;

          const userData = snapshot.data();
          const currentPlan = userData.subscription?.plan || 'free';
          const previousPlan = localStorage.getItem(`previousPlan_${firebaseUser.uid}`) || 'free';

          // Detectar upgrade: de 'free' a cualquier plan pago
          const isUpgrade = previousPlan === 'free' && currentPlan && currentPlan !== 'free';

          if (isUpgrade) {
            try {
              // Buscar el último snapshot de downgrade que no haya sido restaurado
              const snapshotsQuery = query(
                collection(db, 'downgrade_snapshots'),
                where('userId', '==', firebaseUser.uid),
                orderBy('downgradedAt', 'desc'),
                firestoreLimit(5) // Traer los últimos 5 para revisar
              );

              const snapshotsSnap = await getDocs(snapshotsQuery);

              // Buscar el primero que no esté restaurado
              const unrestoredSnapshot = snapshotsSnap.docs.find(doc => {
                const data = doc.data();
                return !data.restoredAt; // No ha sido restaurado
              });

              // Guardar el plan actual para no repetir la consulta
              localStorage.setItem(`previousPlan_${firebaseUser.uid}`, currentPlan);

              if (unrestoredSnapshot) {
                const snapshotId = unrestoredSnapshot.id;
                
                // ⭐ CRÍTICO: Verificar si ya procesamos este snapshot
                const restoredKey = `restored_${snapshotId}`;
                const alreadyRestored = localStorage.getItem(restoredKey);
                
                if (alreadyRestored) {
                  return;
                }
                
                // Ejecutar restauración
                await restoreFromDowngrade(snapshotId);
                
                // ⭐ Marcar como restaurado en localStorage para que NO se repita
                localStorage.setItem(restoredKey, new Date().toISOString());
                
                // Mostrar notificación al usuario y recargar automáticamente solo una vez
                setTimeout(() => {
                  alert('✅ ¡Tu plan ha sido renovado! Todos tus items han sido reactivados.');
                  window.location.reload();
                }, 1000);
              }
            } catch (error) {
              console.error('❌ Error restaurando items:', error);
              localStorage.setItem(`previousPlan_${firebaseUser.uid}`, currentPlan);
            }
          } else {
            localStorage.setItem(`previousPlan_${firebaseUser.uid}`, currentPlan);
          }
        }
      );
    });

    return () => {
      if (unsubSnapshot) {
        unsubSnapshot();
      }
      unsubAuth();
    };
  }, []); // Sin dependencias - solo se monta una vez
}
